import React, { useEffect, useState } from "react";
import { axiosInstance } from "../../../Api/config";
import { MdSettings, MdAdd, MdEdit, MdDelete } from "react-icons/md";

const BikeModels = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingModel, setEditingModel] = useState(null);

  // Form fields
  const [form, setForm] = useState({
    name: "",
    is_active: 1,
  });
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const fetchModels = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/bike-models");
      setModels(response.data || []);
    } catch (err) {
      console.error("Error fetching bike models:", err);
      setError("Failed to fetch bike models.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModels();
  }, []);

  const handleOpenAdd = () => {
    setEditingModel(null);
    setForm({
      name: "",
      is_active: 1,
    });
    setError("");
    setModalOpen(true);
  };

  const handleOpenEdit = (model) => {
    setEditingModel(model);
    setForm({
      name: model.name || "",
      is_active: parseInt(model.is_active) ?? 1,
    });
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.name.trim()) {
      setError("Bike model name is required.");
      return;
    }

    try {
      let res;
      if (editingModel) {
        res = await axiosInstance.post(`/bike-models/${editingModel.id}`, form);
      } else {
        res = await axiosInstance.post("/bike-models", form);
      }

      if (res.data.success) {
        setSuccessMsg(editingModel ? "Bike model updated!" : "Bike model added!");
        setModalOpen(false);
        fetchModels();
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        setError(res.data.message || "Operation failed.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to save bike model details.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this bike model?")) return;
    try {
      const res = await axiosInstance.delete(`/bike-models/${id}`);
      if (res.data.success) {
        setSuccessMsg("Bike model deleted!");
        fetchModels();
        setTimeout(() => setSuccessMsg(""), 3000);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to delete bike model.");
    }
  };

  const handleToggleActive = async (id) => {
    try {
      const res = await axiosInstance.post(`/bike-models/toggle/${id}`);
      if (res.data.success) {
        fetchModels();
      }
    } catch (err) {
      console.error(err);
      setError("Failed to toggle status.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-600">Settings</p>
          <h1 className="mt-2 text-3xl font-bold text-slate-950">Bike Models</h1>
        </div>
        <button
          onClick={handleOpenAdd}
          className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 px-4 py-2.5 rounded-lg text-sm font-bold shadow-md transition-all"
        >
          <MdAdd className="text-lg" />
          Add Bike Model
        </button>
      </div>

      {successMsg && (
        <div className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-4 py-3 rounded-lg text-sm font-semibold">
          {successMsg}
        </div>
      )}

      {error && (
        <div className="bg-rose-50 text-rose-600 border border-rose-100 px-4 py-3 rounded-lg text-sm font-medium">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600" />
        </div>
      ) : models.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 shadow-sm">
          <MdSettings className="text-5xl mx-auto text-slate-300 mb-3" />
          <p>No bike models found. Click "Add Bike Model" to create one.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold">
                  <th className="p-4">Model Name</th>
                  <th className="p-4">Date Created</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-150">
                {models.map((model) => (
                  <tr key={model.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="p-4 font-semibold text-slate-900">{model.name}</td>
                    <td className="p-4 text-slate-500">
                      {new Date(model.created_at).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td className="p-4">
                      <button
                        onClick={() => handleToggleActive(model.id)}
                        className={`text-xs font-bold rounded-full px-3 py-1 border transition ${
                          parseInt(model.is_active) === 1
                            ? "bg-emerald-50 text-emerald-700 border-emerald-250 hover:bg-emerald-100"
                            : "bg-slate-100 text-slate-500 border-slate-300 hover:bg-slate-200"
                        }`}
                        title="Click to toggle status"
                      >
                        {parseInt(model.is_active) === 1 ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="p-4 text-right flex justify-end gap-2">
                      <button
                        onClick={() => handleOpenEdit(model)}
                        className="p-1.5 text-slate-550 hover:text-indigo-650 hover:bg-slate-50 rounded border border-slate-200 transition"
                        title="Edit"
                      >
                        <MdEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(model.id)}
                        className="p-1.5 text-slate-555 hover:text-rose-600 hover:bg-rose-50 rounded border border-slate-200 transition"
                        title="Delete"
                      >
                        <MdDelete />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL */}
      {modalOpen && (
        <div className="fixed inset-0 z-[100] bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-100 animate-in fade-in zoom-in-95 duration-200">
            <div className="border-b border-slate-100 px-6 py-4 flex justify-between items-center bg-slate-50">
              <h2 className="text-lg font-bold text-slate-950">
                {editingModel ? "Edit Bike Model" : "Add Bike Model"}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-slate-600 text-xl">
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="bg-rose-50 text-rose-600 border border-rose-100 px-4 py-2.5 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <label className="block space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Model Name</span>
                <input
                  type="text"
                  required
                  placeholder="e.g. Classic 350"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:bg-white transition-all"
                />
              </label>

              <label className="block space-y-1">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</span>
                <select
                  value={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: parseInt(e.target.value) })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-amber-500 focus:bg-white transition-all"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </label>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-lg text-sm font-bold shadow-md transition"
                >
                  {editingModel ? "Save Changes" : "Create Model"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BikeModels;
